import type { ChangeEvent, FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import {
  PopoverMenu,
  PopoverMenuButton,
  PopoverMenuLink,
} from '@trello/nachos/popover-menu';
import { Textfield } from '@trello/nachos/textfield';
import { buildFuzzyMatcher } from '@trello/strings';

import { isValidUrlForIframe } from 'app/scripts/lib/plugins/pluginValidators';

import styles from './NewPluginPopoverList.less';

type ListItem = {
  text: string;
  callback?: (args: { el: HTMLElement }) => Promise<void>;
  url?: string;
  alwaysVisible?: boolean;
};

type Search = {
  count?: number;
  placeholder?: string;
  empty?: string;
  searching?: string;
  debounce?: number;
};

interface NewPluginPopoverListViewProps {
  items: ListItem[] | ((args: { options: { search: string } }) => ListItem[]);
  search?: Search;
}

export const NewPluginPopoverList: FunctionComponent<
  NewPluginPopoverListViewProps
> = ({ items, search }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const isDynamicSearch = typeof items === 'function';

  const alwaysVisibleItems = useMemo(() => {
    if (isDynamicSearch) {
      return [];
    } else {
      return items.filter((item) => item.alwaysVisible);
    }
  }, [items, isDynamicSearch]);

  const getInitialItems = useMemo(() => {
    let initialItems: ListItem[] = [];
    if (isDynamicSearch) {
      return [];
    }
    if (!search) {
      return items;
    }
    if (search?.count) {
      const itemsToAdd = items
        .filter((item) => !item.alwaysVisible)
        .slice(0, search.count);

      initialItems = [...itemsToAdd, ...alwaysVisibleItems];
    }
    return initialItems;
  }, [alwaysVisibleItems, items, isDynamicSearch, search]);

  const [displayItems, setDisplayItems] = useState<ListItem[]>(getInitialItems);
  const [searchDisplay, setSearchDisplay] = useState<ListItem[]>([]);

  const handleSearch = useCallback(
    async (searchString: string) => {
      setIsSearching(true);
      let searchResults: ListItem[] = [];
      if (isDynamicSearch) {
        const dynamicSearchResults = await items({
          options: {
            search: searchString,
          },
        });
        const dynamicSearchResultsAlwaysVisible = dynamicSearchResults.filter(
          (item) => item.alwaysVisible,
        );
        const dynamicSearchResultsWithoutAlwaysVisible =
          dynamicSearchResults.filter((item) => !item.alwaysVisible);
        searchResults = search?.count
          ? dynamicSearchResultsWithoutAlwaysVisible.slice(0, search.count)
          : dynamicSearchResultsWithoutAlwaysVisible;
        setDisplayItems([
          ...searchResults,
          ...dynamicSearchResultsAlwaysVisible,
        ]);
      } else {
        let matchedItems = items;
        const matcher = buildFuzzyMatcher(searchString);
        matchedItems = items.filter(
          ({ text, alwaysVisible }) => matcher(text) && !alwaysVisible,
        );

        // If the search count is set, we only want to show the first N matched items
        if (search?.count && matchedItems.length > search.count) {
          matchedItems = matchedItems.slice(0, search.count);
        }
        searchResults = matchedItems;
        setDisplayItems([...searchResults, ...alwaysVisibleItems]);
      }
      setIsSearching(false);
      if (searchResults.length === 0) {
        setSearchDisplay([]);
      } else {
        setSearchDisplay(searchResults);
      }
    },
    [items, search?.count, isDynamicSearch, alwaysVisibleItems],
  );

  const debouncedDelay = search?.debounce ? search.debounce : 300;

  const debouncedSearch = useDebouncedCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    debouncedDelay,
  );

  useEffect(() => {
    handleSearch(searchQuery);
  }, [searchQuery, handleSearch]);

  const renderSearchingState = () => {
    return <div className={styles.searchingState}>{search?.searching}</div>;
  };

  const renderEmptyState = () => {
    return <div className={styles.searchingState}>{search?.empty}</div>;
  };

  // Each search popover item should include a url or callback or both
  const renderMenuButton = (item: ListItem, index: number) => {
    if (item.callback) {
      return (
        <PopoverMenuButton
          onClick={async (e) => await item.callback?.({ el: e.currentTarget })}
          key={`${item.text}-${index}`}
        >
          {item.text}
        </PopoverMenuButton>
      );
    } else if (item.url && isValidUrlForIframe(item.url)) {
      return (
        <PopoverMenuLink href={item.url} key={`${item.text}-${index}`}>
          {item.text}
        </PopoverMenuLink>
      );
    }
    return;
  };

  return (
    <div>
      {search && (
        <Textfield
          placeholder={search.placeholder}
          onChange={debouncedSearch}
        />
      )}
      <PopoverMenu>
        {searchDisplay.length === 0 &&
          search?.empty &&
          !isSearching &&
          renderEmptyState()}
        {isSearching && search?.searching ? (
          renderSearchingState()
        ) : (
          <div>
            {displayItems.map((item, index) => {
              return renderMenuButton(item, index);
            })}
          </div>
        )}
      </PopoverMenu>
    </div>
  );
};
